import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Check, FileText, ShieldCheck, X } from 'lucide-react';
import { recordAgreement } from './LegalAgreementView';
import { reflowAgreementBody } from '../../utils/reflowAgreementBody';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paths where the gate must never render (login/register/marketing/legal
// pages themselves). We deliberately keep this list narrow — everything
// inside the app should surface the gate so the venue/host can't hide
// from it.
const SKIP_PATH_PREFIXES = [
  '/', '/welcome', '/about', '/contact', '/support',
  '/login', '/register', '/forgot-password', '/reset-password',
  '/verify-email', '/two-factor', '/unlock',
  '/google', '/app-google-login', '/auth/google', '/app-login-complete',
  '/account-deleted', '/delete-account',
  '/payment-complete', '/guest/payment-method',
  '/invite', '/host-invite', '/venue-invite', '/staff-invite',
  '/privacy', '/terms', '/imprint',
];

function pathIsExempt(pathname) {
  if (!pathname) return true;
  if (pathname === '/') return true;
  return SKIP_PATH_PREFIXES.some((prefix) => {
    if (prefix === '/') return false;
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

function friendlyKindLabel(kind) {
  return kind === 'venue' ? 'Venue Partner Agreement' : 'Host Listing Agreement';
}

/**
 * Global "we updated the agreement" gate.
 *
 * Mounted at the app root. On every route change (and every 5 minutes as a
 * safety net) it polls `/api/agreements/pending-acceptance`. When the user
 * owes a re-acceptance:
 *   - Within the 3-day grace period → a soft banner appears above the app
 *     shell. The user CAN dismiss the banner for this session but the app
 *     keeps working.
 *   - Past the 3-day grace period → the modal is un-dismissable ("Sign in"
 *     is the only path through), and the backend is already returning 403
 *     on feature-gated endpoints (venue map hides them, host invites blocked).
 */
export default function AgreementReAcceptGate({ user }) {
  const location = useLocation();
  const [pending, setPending] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [englishOk, setEnglishOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user) { setPending([]); return; }
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/agreements/pending-acceptance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending(Array.isArray(res.data?.pending) ? res.data.pending : []);
    } catch (_err) {
      /* silent — the gate is best-effort. Feature endpoints still hard-gate
         on the backend so the contract is enforced regardless. */
    }
  }, [user]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending, location.pathname]);

  // Safety-net refresh every 5 minutes so a rollout that happens while the
  // user is browsing still catches up without a page reload.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(fetchPending, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchPending, user]);

  // External trigger — surfaces like the Host Earnings payout-hold banner
  // fire `agreement:open-reaccept` when the user taps "Review & sign".
  // We pick the requested kind out of the pending list and open the modal.
  useEffect(() => {
    if (!user) return undefined;
    const handler = (event) => {
      const requestedKind = event?.detail?.kind || '';
      const list = Array.isArray(pending) ? pending : [];
      let index = 0;
      if (requestedKind) {
        const found = list.findIndex((entry) => entry.kind === requestedKind);
        if (found >= 0) index = found;
      }
      if (list.length === 0) {
        // Re-fetch — this covers the case where a stale local state hasn't
        // caught up with a fresh rollout yet.
        fetchPending();
        return;
      }
      // Un-dismiss the entry for the current session so the modal reappears.
      const entry = list[index];
      if (entry) {
        setDismissedIds((current) => {
          const next = new Set(current);
          next.delete(`${entry.kind}:${entry.version}`);
          return next;
        });
      }
      setActiveIndex(index);
      setAccepted(false);
      setEnglishOk(false);
      setModalOpen(true);
    };
    window.addEventListener('agreement:open-reaccept', handler);
    return () => window.removeEventListener('agreement:open-reaccept', handler);
  }, [user, pending, fetchPending]);

  const exempt = pathIsExempt(location.pathname);
  const visiblePending = useMemo(
    () => pending.filter((entry) => !dismissedIds.has(`${entry.kind}:${entry.version}`)),
    [pending, dismissedIds],
  );

  const currentEntry = visiblePending[activeIndex] || null;

  // Auto-open the modal ONCE per session for hard-gated pending, so the
  // user meets the popup the moment they land in-app.
  useEffect(() => {
    if (!user || exempt || !currentEntry) return;
    if (currentEntry.is_hard_gated) {
      setModalOpen(true);
    } else {
      // Soft gate: pop the modal exactly once per session per version so we
      // don't nag the user across every route change.
      try {
        const key = `allure_agreement_gate_seen_${currentEntry.kind}_${currentEntry.version}`;
        if (!sessionStorage.getItem(key)) {
          setModalOpen(true);
          sessionStorage.setItem(key, '1');
        }
      } catch { /* noop */ }
    }
    setAccepted(false);
    setEnglishOk(false);
  }, [user, exempt, currentEntry]);

  const closeModal = () => {
    if (currentEntry?.is_hard_gated) return; // cannot dismiss
    setModalOpen(false);
  };

  const dismissBanner = () => {
    if (!currentEntry) return;
    setDismissedIds((current) => {
      const next = new Set(current);
      next.add(`${currentEntry.kind}:${currentEntry.version}`);
      return next;
    });
  };

  const handleAccept = async () => {
    if (!currentEntry || !accepted || !englishOk) return;
    setSubmitting(true);
    try {
      await recordAgreement({
        kind: currentEntry.kind,
        version: currentEntry.version,
        accepted_agreement: accepted,
        accepted_english_comprehension: englishOk,
      });
      toast.success('Thanks! Your acceptance is recorded.');
      // Notify listeners (e.g. Host Earnings banner) so they re-fetch and
      // clear any payout-hold surface without a manual reload.
      try {
        window.dispatchEvent(new CustomEvent('agreement:accepted', {
          detail: { kind: currentEntry.kind, version: currentEntry.version },
        }));
      } catch { /* noop */ }
      // Local optimistic update — drop this entry from the pending list.
      setPending((current) => current.filter(
        (entry) => !(entry.kind === currentEntry.kind && entry.version === currentEntry.version),
      ));
      setActiveIndex(0);
      setModalOpen(false);
      setAccepted(false);
      setEnglishOk(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record your acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineAndSignOut = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch { /* noop */ }
    window.location.href = '/login';
  };

  if (!user || exempt || visiblePending.length === 0) return null;

  const banner = currentEntry && !modalOpen ? (
    <div
      data-testid="agreement-reaccept-banner"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        left: 10,
        right: 10,
        zIndex: 9990,
        padding: '10px 12px',
        borderRadius: 14,
        background: currentEntry.is_hard_gated ? 'rgba(233, 76, 87, 0.12)' : 'rgba(120, 88, 214, 0.12)',
        border: `1px solid ${currentEntry.is_hard_gated ? 'rgba(233, 76, 87, 0.5)' : 'rgba(120, 88, 214, 0.5)'}`,
        color: currentEntry.is_hard_gated ? 'var(--status-danger-text, #B00020)' : 'var(--accent-purple)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <AlertTriangle size={16} strokeWidth={2.2} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 800 }}>
          {currentEntry.is_hard_gated
            ? `${friendlyKindLabel(currentEntry.kind)} — action required`
            : `Small update to your ${friendlyKindLabel(currentEntry.kind)}`}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: 1.35, fontWeight: 600 }}>
          {currentEntry.is_hard_gated
            ? 'Some features are paused until you accept the new version.'
            : 'Minor legal update — takes 30 seconds. You can review and accept below.'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="agreement-reaccept-banner-open"
        style={{
          padding: '6px 12px', borderRadius: 10, border: 'none',
          background: currentEntry.is_hard_gated ? 'var(--status-danger-text, #B00020)' : 'var(--accent-purple)',
          color: '#FFF', fontFamily: "'Inria Serif', serif", fontWeight: 800, fontSize: 12,
          cursor: 'pointer',
        }}
      >
        Review
      </button>
      {!currentEntry.is_hard_gated ? (
        <button
          type="button"
          onClick={dismissBanner}
          data-testid="agreement-reaccept-banner-dismiss"
          aria-label="Dismiss banner for this session"
          style={{
            background: 'transparent', border: 'none', color: 'inherit',
            padding: 4, cursor: 'pointer', opacity: 0.7,
          }}
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  ) : null;

  const modal = modalOpen && currentEntry ? (
    <div
      data-testid="agreement-reaccept-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'stretch',
        justifyContent: 'center', zIndex: 10001,
        padding: 'calc(env(safe-area-inset-top, 0px) + 18px) 12px calc(env(safe-area-inset-bottom, 0px) + 18px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 620, width: '100%', background: 'var(--surface, #FFFFFF)',
          borderRadius: 22, boxShadow: '0 30px 60px rgba(0,0,0,0.28)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: '1px solid var(--card-border, #E7E9EE)',
        }}>
          <ShieldCheck size={18} color="var(--accent-purple)" strokeWidth={2.2} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              data-testid="agreement-reaccept-modal-title"
              style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15.5, fontWeight: 800, color: 'var(--text, #0F1115)' }}
            >
              {friendlyKindLabel(currentEntry.kind)} — {currentEntry.version}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'var(--text-3, #5B6471)', fontWeight: 600 }}>
              {currentEntry.is_hard_gated
                ? 'Some features are paused until you sign.'
                : `Minor update. Grace period ends ${new Date(currentEntry.grace_expires_at).toLocaleDateString('en-GB')}.`}
            </p>
          </div>
          {!currentEntry.is_hard_gated ? (
            <button
              type="button"
              onClick={closeModal}
              data-testid="agreement-reaccept-modal-close"
              aria-label="Close"
              style={{
                background: 'var(--surface, #FFF)', border: '1px solid var(--card-border, #E7E9EE)',
                borderRadius: 10, width: 32, height: 32, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: 'var(--text, #0F1115)',
              }}
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        {currentEntry.changelog_note ? (
          <div
            data-testid="agreement-reaccept-changelog"
            style={{
              margin: '12px 14px 0', padding: '10px 12px', borderRadius: 12,
              background: 'rgba(120, 88, 214, 0.08)',
              border: '1px solid rgba(120, 88, 214, 0.22)',
              color: 'var(--accent-purple)', fontSize: 13, lineHeight: 1.45,
              fontWeight: 600,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 2, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 11 }}>
              What changed
            </strong>
            {currentEntry.changelog_note}
          </div>
        ) : null}

        <div style={{ padding: 14, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 8, paddingBottom: 8,
            borderBottom: '1px solid var(--card-border, #E7E9EE)',
          }}>
            <FileText size={14} color="var(--accent-purple)" strokeWidth={2.1} />
            <p style={{
              margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 11.5,
              fontWeight: 700, color: 'var(--text-3, #5B6471)',
              letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>
              Full agreement · Version {currentEntry.version}
            </p>
          </div>
          <pre
            data-testid="agreement-reaccept-body"
            style={{
              margin: 0, fontFamily: "'Inria Serif', serif",
              fontSize: 13, lineHeight: 1.55,
              color: 'var(--text, #0F1115)', whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {reflowAgreementBody(currentEntry.body) || 'This version has no body snapshot on file.'}
          </pre>
        </div>

        <div style={{ padding: 12, borderTop: '1px solid var(--card-border, #E7E9EE)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label
            data-testid="agreement-reaccept-accept-label"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
              borderRadius: 12, background: 'var(--surface, #FFF)',
              border: `1.5px solid ${accepted ? 'var(--accent-purple)' : 'var(--card-border, #E7E9EE)'}`,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={accepted}
              disabled={submitting}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="agreement-reaccept-checkbox"
              style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--accent-purple)' }}
            />
            <span style={{ flex: 1, fontFamily: "'Inria Serif', serif", fontSize: 13, lineHeight: 1.4, color: 'var(--text, #0F1115)', fontWeight: 600 }}>
              I have read and agree to the updated {friendlyKindLabel(currentEntry.kind)} (version {currentEntry.version}).
            </span>
          </label>

          <label
            data-testid="agreement-reaccept-english-label"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
              borderRadius: 12, background: 'var(--surface, #FFF)',
              border: `1.5px solid ${englishOk ? 'var(--accent-purple)' : 'var(--card-border, #E7E9EE)'}`,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={englishOk}
              disabled={submitting}
              onChange={(e) => setEnglishOk(e.target.checked)}
              data-testid="agreement-reaccept-english-checkbox"
              style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--accent-purple)' }}
            />
            <span style={{ flex: 1, fontFamily: "'Inria Serif', serif", fontSize: 13, lineHeight: 1.4, color: 'var(--text, #0F1115)', fontWeight: 600 }}>
              I confirm I can read and understand English at a level sufficient to understand this agreement and {currentEntry.kind === 'venue'
                ? 'serve English-speaking guests at my venue.'
                : 'use the Allure host dashboard.'}
            </span>
          </label>

          <div style={{ display: 'flex', gap: 8 }}>
            {currentEntry.is_hard_gated ? (
              <button
                type="button"
                onClick={handleDeclineAndSignOut}
                data-testid="agreement-reaccept-decline"
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 12,
                  border: '1px solid var(--card-border, #E7E9EE)', background: 'var(--surface, #FFF)',
                  color: 'var(--text, #0F1115)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                Decline & sign out
              </button>
            ) : (
              <button
                type="button"
                onClick={closeModal}
                data-testid="agreement-reaccept-later"
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 12,
                  border: '1px solid var(--card-border, #E7E9EE)', background: 'var(--surface, #FFF)',
                  color: 'var(--text, #0F1115)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                Remind me later
              </button>
            )}
            <button
              type="button"
              onClick={handleAccept}
              disabled={!accepted || !englishOk || submitting}
              data-testid="agreement-reaccept-submit"
              style={{
                flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '11px 14px', borderRadius: 12, border: 'none',
                background: (!accepted || !englishOk || submitting) ? 'rgba(120, 88, 214, 0.4)' : 'var(--accent-purple)',
                color: '#FFF', fontWeight: 800, fontSize: 13,
                cursor: (!accepted || !englishOk || submitting) ? 'not-allowed' : 'pointer',
              }}
            >
              <Check size={14} strokeWidth={2.5} />
              {submitting ? 'Saving…' : 'I agree'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {banner}
      {modal}
    </>
  );
}
